import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, LogIn, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [step, setStep] = useState(1); // 1: creds, 2: 2FA
  const [loading, setLoading] = useState(false);

  const submitLogin = async (includeTotp = false) => {
    setLoading(true);
    try {
      const payload = { email, password };
      if (includeTotp) payload.totp_code = totpCode;
      const { data } = await axios.post(`${API_URL}/api/auth/superadmin/login`, payload, { withCredentials: true });
      if (data.access_token) {
        // Session-scoped: closing the browser/tab forces re-login.
        sessionStorage.setItem('access_token', data.access_token);
        try { localStorage.removeItem('access_token'); } catch { /* noop */ }
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      }
      toast.success('Sistem yöneticisi girişi başarılı');
      window.location.href = '/superadmin';
    } catch (err) {
      const detail = err.response?.data?.detail || 'Giriş başarısız';
      if (detail === '2FA_REQUIRED') {
        setStep(2);
        toast.info('2FA kodunu girin');
      } else {
        toast.error(detail);
        if (step === 2 && detail.includes('2FA')) {
          setTotpCode('');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreds = (e) => {
    e.preventDefault();
    submitLogin(false);
  };

  const handle2FA = (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) {
      toast.error('6 haneli kod girin');
      return;
    }
    submitLogin(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0f1c] p-4 relative overflow-hidden">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,158,11,0.08),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(44,76,59,0.1),transparent_50%)]" />

      <div className="w-full max-w-sm space-y-6 relative z-10">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <Shield className="w-8 h-8 text-white" strokeWidth={2.2} />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Sistem Yönetimi</h1>
          <p className="text-sm text-white/40 mt-1">Güvenli erişim paneli</p>
        </div>

        <Card className="border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl" data-testid="admin-login-card">
          <CardContent className="p-6">
            {step === 1 ? (
              <form onSubmit={handleCreds} className="space-y-4" data-testid="admin-login-form">
                <div className="space-y-1.5">
                  <Label className="text-sm text-white/80">E-posta</Label>
                  <Input
                    type="email"
                    placeholder="admin@paydaai.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="admin-email-input"
                    className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    autoComplete="username"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-white/80">Şifre</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password-input"
                    className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    autoComplete="current-password"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white gap-2 font-semibold shadow-lg shadow-amber-500/20"
                  disabled={loading}
                  data-testid="admin-login-btn"
                >
                  {loading ? 'Doğrulanıyor...' : (<><LogIn className="w-4 h-4" /> Devam Et</>)}
                </Button>
              </form>
            ) : (
              <form onSubmit={handle2FA} className="space-y-4" data-testid="admin-2fa-form">
                <button
                  type="button"
                  onClick={() => { setStep(1); setTotpCode(''); }}
                  className="text-xs text-white/50 hover:text-white/80 flex items-center gap-1"
                  data-testid="back-to-step1"
                >
                  <ArrowLeft className="w-3 h-3" /> Geri
                </button>
                <div className="text-center py-2">
                  <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1 mb-2">
                    <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs text-amber-300 font-medium">2FA Doğrulama</span>
                  </div>
                  <p className="text-sm text-white/70">Google Authenticator'daki 6 haneli kodu girin</p>
                </div>
                <div className="space-y-1.5">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    required
                    data-testid="admin-totp-input"
                    className="h-14 bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.5em] font-mono"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white gap-2 font-semibold"
                  disabled={loading || totpCode.length !== 6}
                  data-testid="admin-2fa-btn"
                >
                  {loading ? 'Doğrulanıyor...' : (<><Shield className="w-4 h-4" /> Doğrula</>)}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-white/20">
          Yetkisiz erişim girişimleri kayıt altına alınmaktadır.
        </p>
      </div>
    </div>
  );
};
